import { registerLocaleVariant } from "../../registry";
import { makeId } from "../../helpers";
import type { ConditionBody } from "../../types";

// ─── Helper ─────────────────────────────────────────────────────────

function frCondition(
  slug: string,
  variant: {
    title: string;
    subtitle: string;
    body: Partial<ConditionBody> & Pick<ConditionBody, "description">;
  },
): void {
  registerLocaleVariant(makeId(slug), "fr", variant);
}

// ─── Reproductive conditions ───────────────────────────────────────

frCondition("dx-diminished-ovarian-reserve", {
  title: "Réserve ovarienne diminuée",
  subtitle: "Stock d'ovocytes plus bas qu'attendu pour ton âge",
  body: {
    conditionName: "Réserve ovarienne diminuée (DOR)",
    description:
      "La réserve ovarienne diminuée signifie que les ovaires ont moins d'ovocytes restants qu'attendu pour l'âge de la femme. Cela ne veut pas nécessairement dire que les ovocytes sont de mauvaise qualité — quantité et qualité sont liées mais distinctes. La DOR est l'une des découvertes les plus courantes lors d'un bilan de fertilité.",
    symptoms: [
      "Souvent aucun symptôme — détectée par prise de sang et échographie",
      "Cycles menstruels plus courts (par exemple 24 à 25 jours)",
      "Règles plus légères dans certains cas",
      "Difficultés à concevoir",
    ],
    howDiagnosed:
      "Diagnostiquée par une combinaison de prise de sang d'AMH (généralement < 1,0 ng/mL), comptage des follicules antraux (CFA) à l'échographie (généralement < 5 à 7 par ovaire), et taux de FSH au 3e jour du cycle (élevé au-dessus de 10 mUI/mL). Ta médecin tient aussi compte de ton âge et de ta réponse aux cycles de stimulation antérieurs.",
    impactOnFertility:
      "La DOR signifie moins d'ovocytes disponibles à la ponction en FIV ou en autoconservation, ce qui peut nécessiter plus de cycles pour atteindre le nombre souhaité d'ovocytes ou d'embryons. Cependant, la qualité ovocytaire — qui compte le plus pour la grossesse — est principalement déterminée par l'âge, pas par l'AMH ou le CFA seuls. Beaucoup de femmes avec une DOR obtiennent des grossesses réussies.",
    prevalence:
      "Touche environ 10 à 30 % des femmes en bilan de fertilité, en augmentation avec l'âge.",
    treatmentOptions: [
      "FIV avec protocoles de stimulation adaptés (mini-FIV, hautes doses)",
      "Autoconservation d'ovocytes ou d'embryons pour préserver la fertilité restante",
      "Don d'ovocytes si le stock propre est très bas",
      "Compléments comme la CoQ10 et la DHEA (preuves modérées)",
      "Optimisation du mode de vie pour soutenir la qualité ovocytaire",
    ],
    prognosis:
      "Le pronostic dépend fortement de l'âge et de la qualité ovocytaire. Les femmes plus jeunes avec DOR (moins de 35 ans) ont souvent encore une bonne qualité ovocytaire et des taux de réussite par embryon raisonnables. Les femmes plus âgées peuvent avoir besoin de plusieurs cycles ou d'envisager un don d'ovocytes.",
  },
});

frCondition("dx-endometriosis", {
  title: "Endométriose",
  subtitle: "Du tissu similaire à l'endomètre pousse hors de l'utérus",
  body: {
    conditionName: "Endométriose",
    description:
      "L'endométriose survient quand un tissu similaire à la muqueuse utérine (endomètre) pousse en dehors de l'utérus — sur les ovaires, les trompes de Fallope, l'intestin, ou le péritoine pelvien. Ce tissu répond aux cycles hormonaux, causant inflammation, cicatrices, et adhérences qui peuvent affecter la fertilité.",
    symptoms: [
      "Règles douloureuses (dysménorrhée)",
      "Douleurs pelviennes chroniques",
      "Douleur pendant ou après les rapports",
      "Douleur à la défécation ou à la miction pendant les règles",
      "Règles abondantes ou irrégulières",
      "Fatigue",
      "Difficultés à concevoir",
    ],
    howDiagnosed:
      "Le diagnostic définitif nécessite une cœlioscopie avec biopsie. Mais l'endométriose est souvent suspectée sur la base des symptômes, de l'examen pelvien, et de l'imagerie (échographie montrant des endométriomes / kystes chocolat). L'IRM aide à cartographier la maladie en profondeur. Le délai moyen de diagnostic est de 7 à 10 ans après l'apparition des symptômes.",
    impactOnFertility:
      "L'endométriose peut altérer la fertilité par plusieurs mécanismes : distorsion de l'anatomie pelvienne, obstruction des trompes, environnement hostile pour les ovocytes et embryons, et baisse de la réserve ovarienne (surtout avec les endométriomes). Environ 30 à 50 % des femmes avec endométriose vivent une infertilité.",
    prevalence:
      "Touche environ 1 femme sur 10 en âge de procréer dans le monde.",
    treatmentOptions: [
      "Chirurgie d'exérèse cœlioscopique pour retirer le tissu endométriosique",
      "FIV (souvent recommandée pour les cas modérés à sévères)",
      "Suppression hormonale avant FIV (blocage par agoniste GnRH)",
      "IIU pour endométriose légère",
      "Alimentation anti-inflammatoire et modifications du mode de vie",
      "Gestion de la douleur (AINS, traitements hormonaux)",
    ],
    prognosis:
      "L'endométriose légère a un pronostic de fertilité relativement bon, surtout avec IIU ou FIV. Les cas modérés à sévères bénéficient d'un traitement chirurgical avant le traitement de fertilité. Les taux de réussite en FIV sont un peu plus bas avec endométriose mais restent encourageants, surtout chez les femmes plus jeunes.",
  },
});

frCondition("dx-pcos", {
  title: "PMOS (ex SOPK)",
  subtitle: "Trouble hormonal affectant l'ovulation et le métabolisme",
  body: {
    conditionName: "PMOS (ex SOPK)",
    description:
      "Le PMOS (ex SOPK) est un trouble hormonal courant où les ovaires produisent un excès d'androgènes (hormones masculines), entraînant des troubles de l'ovulation, du développement folliculaire, et des changements métaboliques. Malgré le nom, toutes les femmes avec PMOS (ex SOPK) n'ont pas de kystes ovariens — les « kystes » sont en réalité de petits follicules immatures.",
    symptoms: [
      "Règles irrégulières ou absentes",
      "Saignements abondants ou imprévisibles",
      "Ovulation irrégulière",
      "Pilosité excessive du visage ou du corps",
      "Acné ou peau grasse",
      "Prise de poids ou difficulté à en perdre",
      "Cheveux qui s'amincissent sur le cuir chevelu",
      "Plaques de peau assombries",
    ],
    howDiagnosed:
      "Diagnostiqué selon les critères de Rotterdam — nécessite au moins 2 sur 3 : ovulation irrégulière ou absente, signes cliniques ou biologiques d'excès d'androgènes, et ovaires d'aspect polykystique à l'échographie (≥ 12 petits follicules par ovaire ou volume ovarien augmenté). Les analyses sanguines incluent testostérone, DHEA-S, insuline, glycémie, et AMH.",
    impactOnFertility:
      "Le PMOS (ex SOPK) est l'une des causes d'infertilité les plus traitables. Le problème principal est une ovulation irrégulière ou absente, qui empêche la libération prévisible d'ovocytes. Cependant, les femmes avec PMOS (ex SOPK) ont souvent une grande quantité d'ovocytes et peuvent très bien répondre aux médicaments de fertilité — même si elles sont à plus haut risque de syndrome d'hyperstimulation ovarienne (SHO).",
    prevalence:
      "Touche 8 à 13 % des femmes en âge de procréer ; jusqu'à 70 % ne sont pas diagnostiquées.",
    treatmentOptions: [
      "Létrozole (première ligne pour induire l'ovulation)",
      "Citrate de clomifène pour induire l'ovulation",
      "Metformine (en cas d'insulinorésistance)",
      "IIU avec induction d'ovulation",
      "FIV (pour les cas résistants)",
      "Gestion du poids et changements de mode de vie",
      "Compléments d'inositol (preuves modérées)",
    ],
    prognosis:
      "Le PMOS (ex SOPK) a l'un des meilleurs pronostics de fertilité parmi les diagnostics d'infertilité. La plupart des femmes avec PMOS (ex SOPK) conçoivent avec une induction d'ovulation seule ou avec une IIU. La FIV, quand elle est nécessaire, donne souvent beaucoup d'ovocytes — même si des protocoles prudents sont nécessaires pour éviter le SHO.",
  },
});

frCondition("dx-unexplained-infertility", {
  title: "Infertilité inexpliquée",
  subtitle: "Aucune cause identifiable après les examens standard",
  body: {
    conditionName: "Infertilité inexpliquée",
    description:
      "L'infertilité inexpliquée se diagnostique quand les examens standard de fertilité (spermogramme, évaluation de l'ovulation, perméabilité tubaire, et évaluation utérine) ne révèlent aucune cause claire à la difficulté de concevoir. Cela ne veut pas dire que tout va bien — cela veut dire que les examens actuels n'ont pas identifié le facteur spécifique. Des problèmes subtils de qualité ovocytaire, de fonction spermatique, de fécondation, ou d'implantation peuvent être en jeu.",
    symptoms: [
      "Incapacité à concevoir après 12 mois d'essais (ou 6 mois si plus de 35 ans)",
      "Résultats normaux sur tous les examens standard",
    ],
    howDiagnosed:
      "Diagnostiqué par exclusion — tous les examens standard reviennent normaux : ovulation régulière (confirmée par prise de sang ou suivi), au moins une trompe de Fallope perméable (HSG ou cœlioscopie), cavité utérine normale (HSG ou hystérosonographie), et spermogramme normal.",
    impactOnFertility:
      "Malgré l'étiquette « inexpliquée », les taux cumulés de grossesse sont raisonnables. Environ 60 % des couples avec infertilité inexpliquée conçoivent dans les 3 ans sans traitement. Le traitement peut accélérer ce délai significativement.",
    prevalence: "Représente environ 25 à 30 % des diagnostics d'infertilité.",
    treatmentOptions: [
      "Rapports programmés avec suivi de l'ovulation",
      "IIU avec induction d'ovulation (létrozole ou clomid)",
      "FIV (recommandée après des cycles d'IIU échoués ou en cas d'attente prolongée)",
      "Optimisation du mode de vie",
    ],
    prognosis:
      "Pronostic généralement favorable. Les taux de réussite de l'IIU sont autour de 10 à 15 % par cycle. Les taux de réussite de la FIV sont comparables aux moyennes par âge, souvent entre 40 et 60 % par transfert pour les femmes de moins de 35 ans.",
  },
});

frCondition("dx-premature-ovarian-insufficiency", {
  title: "Insuffisance ovarienne prématurée (IOP)",
  subtitle: "La fonction ovarienne décline avant 40 ans",
  body: {
    conditionName: "Insuffisance ovarienne prématurée (IOP)",
    description:
      "L'IOP (anciennement appelée insuffisance ovarienne primitive) survient quand les ovaires arrêtent de fonctionner normalement avant 40 ans. Contrairement à la ménopause, l'IOP peut être intermittente — certaines femmes ont des ovulations occasionnelles et même des grossesses spontanées. Elle est associée à une réserve ovocytaire très basse et à une FSH élevée.",
    symptoms: [
      "Règles irrégulières ou absentes avant 40 ans",
      "Bouffées de chaleur et sueurs nocturnes",
      "Sécheresse vaginale",
      "Difficultés à concevoir",
      "Baisse d'énergie et changements d'humeur",
    ],
    howDiagnosed:
      "Diagnostiquée quand les taux de FSH sont constamment élevés (> 25 à 40 mUI/mL) sur deux tests à au moins un mois d'intervalle chez une femme de moins de 40 ans avec règles absentes ou irrégulières depuis au moins 4 mois. L'AMH est généralement très basse ou indétectable.",
    impactOnFertility:
      "L'IOP impacte significativement la fertilité à cause du très petit nombre d'ovocytes restants. Une grossesse spontanée survient dans environ 5 à 10 % des cas. Le don d'ovocytes offre les meilleurs taux de réussite pour celles qui veulent porter une grossesse.",
    prevalence: "Touche environ 1 femme sur 100 de moins de 40 ans.",
    treatmentOptions: [
      "Don d'ovocytes (meilleur taux de réussite)",
      "FIV avec ses propres ovocytes (si une activité ovarienne persiste)",
      "Traitement hormonal substitutif pour la santé osseuse et cardiovasculaire",
      "Congélation d'embryons si détecté tôt",
      "Compléments DHEA et CoQ10 (preuves limitées)",
    ],
    prognosis:
      "Le pronostic de fertilité avec ses propres ovocytes est limité, sans être impossible. Avec un don d'ovocytes, les taux de réussite par grossesse sont excellents (souvent 50 à 60 % par transfert). Le diagnostic précoce et la préservation de fertilité sont essentiels.",
  },
});

// ─── Endocrine conditions ───────────────────────────────────────────

frCondition("dx-hypothyroidism", {
  title: "Hypothyroïdie (subclinique)",
  subtitle: "Thyroïde sous-active affectant les hormones reproductives",
  body: {
    conditionName: "Hypothyroïdie (subclinique)",
    description:
      "L'hypothyroïdie subclinique signifie que ta TSH est légèrement élevée alors que les hormones thyroïdiennes (T3, T4) restent dans la fourchette normale. Même cette dysfonction légère peut impacter la fertilité — les hormones thyroïdiennes jouent un rôle critique dans l'ovulation, l'implantation, et le maintien de la grossesse précoce. En traitement de fertilité, des cibles de TSH plus strictes (< 2,5 mUI/L) sont recommandées.",
    symptoms: [
      "Souvent aucun symptôme notable",
      "Fatigue",
      "Prise de poids ou difficulté à en perdre",
      "Sensation de froid",
      "Peau et cheveux secs",
      "Constipation",
      "Règles légèrement irrégulières",
    ],
    howDiagnosed:
      "Diagnostiquée par prise de sang montrant une TSH élevée (généralement 2,5 à 10 mUI/L pour la subclinique) avec un taux de T4 libre normal. Les anticorps thyroïdiens (anti-TPO, anti-TG) sont aussi dosés, car la thyroïdite de Hashimoto est la cause la plus courante. Les spécialistes de la fertilité utilisent un seuil plus strict de TSH < 2,5 mUI/L.",
    impactOnFertility:
      "Même une TSH légèrement élevée est associée à un temps de conception plus long, à des taux de fausse couche plus élevés, et potentiellement à un moindre succès en FIV. L'auto-immunité thyroïdienne (anticorps positifs) augmente indépendamment le risque de fausse couche. Le traitement par lévothyroxine est simple et efficace.",
    prevalence:
      "L'hypothyroïdie subclinique touche 4 à 8 % des femmes en âge de procréer. L'auto-immunité thyroïdienne est trouvée chez 5 à 15 %.",
    treatmentOptions: [
      "Lévothyroxine (T4 synthétique) — médicament oral quotidien",
      "Cible TSH < 2,5 mUI/L pendant le traitement de fertilité et la grossesse",
      "Supplémentation en sélénium pour les anticorps thyroïdiens (preuves modérées)",
      "Suivi régulier de la TSH (toutes les 4 à 6 semaines pendant la grossesse)",
    ],
    prognosis:
      "Excellent avec traitement. La lévothyroxine est bien tolérée et normalise efficacement les résultats de fertilité. Une fois la TSH dans la cible, les taux de réussite sont comparables à ceux des femmes sans problème thyroïdien.",
  },
});

frCondition("dx-hyperprolactinemia", {
  title: "Hyperprolactinémie",
  subtitle: "Taux de prolactine élevés perturbant l'ovulation",
  body: {
    conditionName: "Hyperprolactinémie",
    description:
      "L'hyperprolactinémie est une condition où l'hypophyse produit trop de prolactine, l'hormone responsable de la production de lait maternel. Une prolactine élevée supprime la GnRH, qui à son tour supprime la FSH et la LH — les hormones nécessaires à l'ovulation. Les causes courantes incluent les adénomes hypophysaires (petites tumeurs bénignes), certains médicaments, et le stress.",
    symptoms: [
      "Règles irrégulières ou absentes",
      "Écoulement laiteux des mamelons (galactorrhée)",
      "Maux de tête ou troubles visuels (si adénome hypophysaire)",
      "Difficultés à concevoir",
      "Baisse de libido",
    ],
    howDiagnosed:
      "Diagnostiquée par prise de sang montrant des taux de prolactine élevés (généralement > 25 ng/mL). Une IRM de l'hypophyse est demandée pour rechercher un prolactinome. Le stress, certains médicaments, et même la prise de sang elle-même peuvent élever temporairement la prolactine, alors une analyse répétée peut être nécessaire.",
    impactOnFertility:
      "Une prolactine élevée supprime directement l'ovulation, rendant la conception difficile ou impossible sans traitement. Cependant, c'est l'une des causes hormonales d'infertilité les plus traitables.",
    prevalence:
      "Représente environ 10 à 20 % des cas de règles absentes (aménorrhée).",
    treatmentOptions: [
      "Cabergoline (médicament de première ligne — très efficace)",
      "Bromocriptine (médicament alternatif)",
      "Chirurgie (rarement nécessaire, uniquement pour les tumeurs volumineuses ou résistantes)",
      "Arrêt des médicaments en cause quand c'est possible",
    ],
    prognosis:
      "Excellent. Le traitement normalise la prolactine dans 80 à 90 % des cas, et l'ovulation reprend généralement en quelques semaines à quelques mois. Les taux de grossesse après traitement sont comparables à ceux de la population générale.",
  },
});

// ─── Structural conditions ──────────────────────────────────────────

frCondition("dx-uterine-fibroids", {
  title: "Fibromes utérins",
  subtitle: "Excroissances non cancéreuses dans ou autour de l'utérus",
  body: {
    conditionName: "Fibromes utérins (léiomyomes)",
    description:
      "Les fibromes sont des excroissances bénignes de muscle lisse dans l'utérus. Leur impact sur la fertilité dépend surtout de leur localisation : les fibromes sous-muqueux (dans la cavité utérine) peuvent altérer significativement l'implantation, tandis que les sous-séreux (sur la surface externe) n'affectent généralement pas la fertilité. Les fibromes intramuraux (dans la paroi) ont un impact variable selon leur taille et leur proximité à la cavité.",
    symptoms: [
      "Règles abondantes ou prolongées",
      "Douleur ou pression pelvienne",
      "Mictions fréquentes",
      "Difficultés à concevoir ou fausses couches à répétition",
      "Beaucoup de fibromes sont asymptomatiques",
    ],
    howDiagnosed:
      "Détectés initialement par échographie pelvienne. Une hystérosonographie ou une hystéroscopie peuvent déterminer si les fibromes déforment la cavité utérine. L'IRM fournit la cartographie la plus détaillée de la taille, du nombre, et de la localisation des fibromes.",
    impactOnFertility:
      "Les fibromes sous-muqueux réduisent les taux d'implantation d'environ 50 % et devraient être retirés avant FIV. Les fibromes intramuraux volumineux (> 4 à 5 cm) peuvent aussi altérer les résultats. Les fibromes sous-séreux n'affectent généralement pas la fertilité.",
    prevalence:
      "Extrêmement courants — jusqu'à 70 à 80 % des femmes à 50 ans, même si beaucoup sont asymptomatiques. Présents chez 5 à 10 % des femmes infertiles.",
    treatmentOptions: [
      "Myomectomie hystéroscopique (pour les fibromes sous-muqueux — peu invasive)",
      "Myomectomie cœlioscopique ou ouverte (pour les fibromes plus volumineux ou multiples)",
      "Surveillance simple si les fibromes n'affectent pas la cavité",
      "FIV après correction chirurgicale",
    ],
    prognosis:
      "Après retrait des fibromes déformant la cavité, les résultats de fertilité s'améliorent significativement. Les taux de réussite en FIV se normalisent une fois la cavité utérine restaurée. Les fibromes peuvent récidiver — le timing du traitement par rapport au projet de fertilité compte.",
  },
});

frCondition("dx-tubal-factor", {
  title: "Infertilité tubaire",
  subtitle: "Trompes de Fallope bouchées ou abîmées",
  body: {
    conditionName: "Infertilité tubaire",
    description:
      "L'infertilité tubaire survient quand une ou les deux trompes de Fallope sont bouchées, abîmées, ou absentes, empêchant ovocyte et spermatozoïde de se rencontrer. Les causes courantes incluent les maladies inflammatoires pelviennes (MIP), une grossesse extra-utérine antérieure, l'endométriose, et la chirurgie pelvienne précédente. L'hydrosalpinx (trompes remplies de liquide) est particulièrement néfaste pour la fertilité.",
    symptoms: [
      "Généralement aucun symptôme sauf si un hydrosalpinx est présent",
      "Antécédents d'infection pelvienne",
      "Difficultés à concevoir",
      "Parfois douleur pelvienne",
    ],
    howDiagnosed:
      "L'hystérosalpingographie (HSG) est le test de dépistage standard — un produit de contraste est injecté à travers le col et des radios montrent si les trompes sont perméables. La cœlioscopie avec épreuve au bleu donne le diagnostic définitif. L'échographie peut détecter un hydrosalpinx.",
    impactOnFertility:
      "Les trompes bouchées empêchent la conception naturelle et l'IIU. Le liquide d'hydrosalpinx peut s'écouler dans l'utérus et réduire le succès de la FIV de 50 % — son retrait ou son clippage avant FIV est fortement recommandé.",
    prevalence: "Représente 25 à 35 % de l'infertilité féminine.",
    treatmentOptions: [
      "FIV (court-circuite entièrement les trompes — traitement principal)",
      "Salpingectomie pour l'hydrosalpinx avant FIV",
      "Chirurgie tubaire / réparation (cas sélectionnés avec atteinte minimale)",
      "Cathétérisme tubaire proximal (pour les obstructions proximales)",
    ],
    prognosis:
      "Les taux de réussite en FIV sont excellents une fois l'hydrosalpinx pris en charge. Pour une atteinte tubaire légère, la réparation chirurgicale peut restaurer la fertilité naturelle dans certains cas, même si la FIV est généralement préférée pour son efficacité.",
  },
});

frCondition("dx-uterine-polyps", {
  title: "Polypes utérins",
  subtitle: "Excroissances bénignes de la muqueuse utérine",
  body: {
    conditionName: "Polypes endométriaux",
    description:
      "Les polypes endométriaux sont des excroissances localisées de la muqueuse utérine (endomètre) qui font saillie dans la cavité utérine. Ils sont généralement bénins mais peuvent interférer avec l'implantation embryonnaire en perturbant la surface endométriale et l'environnement inflammatoire local.",
    symptoms: [
      "Saignements irréguliers ou intermenstruels",
      "Règles abondantes",
      "Saignements post-ménopausiques",
      "Souvent découverte fortuite à l'échographie",
      "Difficultés à concevoir",
    ],
    howDiagnosed:
      "Suspectés à l'échographie endovaginale, confirmés par hystérosonographie ou hystéroscopie. L'hystéroscopie permet le diagnostic et le retrait simultanés avec envoi du tissu en anatomopathologie.",
    impactOnFertility:
      "Les polypes de plus de 1 à 2 cm sont associés à des taux d'implantation réduits. Leur retrait avant FIV ou IIU a montré une amélioration des taux de grossesse. Les petits polypes peuvent avoir moins d'impact.",
    prevalence: "Trouvés chez 10 à 30 % des femmes en bilan de fertilité.",
    treatmentOptions: [
      "Polypectomie hystéroscopique (procédure ambulatoire simple)",
      "Poursuite du traitement de fertilité après retrait",
      "Surveillance possible pour les petits polypes",
    ],
    prognosis:
      "Excellent après retrait. La polypectomie est une procédure simple avec une récupération rapide. Les taux de conception naturelle et assistée s'améliorent après le retrait, et la récidive est relativement basse.",
  },
});

// ─── Male factor conditions ─────────────────────────────────────────

frCondition("dx-male-factor-infertility", {
  title: "Infertilité de facteur masculin",
  subtitle: "Paramètres spermatiques anormaux affectant la conception",
  body: {
    conditionName: "Infertilité de facteur masculin",
    description:
      "L'infertilité de facteur masculin regroupe une série de conditions où une numération, mobilité, ou morphologie spermatique anormale réduit les chances de conception naturelle. C'est un facteur contributif chez environ 50 % des couples vivant une infertilité. Les causes incluent varicocèle, déséquilibres hormonaux, facteurs génétiques, mode de vie, et origines idiopathiques (inconnues).",
    symptoms: [
      "Généralement aucun symptôme physique",
      "Difficultés à concevoir",
      "Antécédents de traumatisme, chirurgie, ou infection testiculaire",
      "Troubles érectiles ou de l'éjaculation (dans certains cas)",
    ],
    howDiagnosed:
      "Le diagnostic principal passe par un spermogramme mesurant volume, concentration, mobilité, et morphologie (critères OMS 2021). Les examens complémentaires peuvent inclure un bilan hormonal (FSH, testostérone, LH), une échographie scrotale, un test de fragmentation de l'ADN, et un dépistage génétique (caryotype, microdélétion du chromosome Y).",
    impactOnFertility:
      "L'impact varie largement selon la sévérité. Les anomalies légères peuvent être surmontées en IIU. Les anomalies modérées nécessitent généralement la FIV. Les cas sévères (numération ou mobilité très basses) nécessitent la FIV avec ICSI, où un seul spermatozoïde est injecté directement dans l'ovocyte.",
    prevalence:
      "Contribue à l'infertilité dans environ 40 à 50 % des couples. Le facteur purement masculin représente environ 20 à 30 %.",
    treatmentOptions: [
      "IIU (pour les anomalies légères)",
      "FIV avec ICSI (pour les cas modérés à sévères)",
      "Réparation de varicocèle (si varicocèle présente)",
      "Traitement hormonal (clomifène, hCG, ou FSH pour les causes hormonales)",
      "Modifications du mode de vie (réduire l'exposition à la chaleur, arrêter le tabac, limiter l'alcool)",
      "Prélèvement chirurgical de sperme (TESA / micro-TESE pour azoospermie)",
      "Supplémentation antioxydante (CoQ10, vitamine E, zinc)",
    ],
    prognosis:
      "Le pronostic est généralement bon avec un traitement adapté. La FIV-ICSI obtient des taux de fécondation de 60 à 70 % par ovocyte quels que soient les paramètres spermatiques. Les changements de mode de vie peuvent améliorer la qualité spermatique en 2 à 3 mois (un cycle complet de spermatogenèse).",
  },
});

frCondition("dx-azoospermia", {
  title: "Azoospermie",
  subtitle: "Aucun spermatozoïde trouvé dans l'éjaculat",
  body: {
    conditionName: "Azoospermie",
    description:
      "L'azoospermie signifie qu'aucun spermatozoïde n'est trouvé dans le sperme après centrifugation. Elle est classée comme obstructive (les spermatozoïdes sont produits mais bloqués) ou non obstructive (production spermatique altérée). Les causes obstructives incluent vasectomie, absence congénitale des canaux déférents, ou cicatrices post-infection. Les causes non obstructives incluent conditions génétiques, troubles hormonaux, ou insuffisance testiculaire.",
    symptoms: [
      "Incapacité à concevoir",
      "Éjaculation normale dans la plupart des cas",
      "Testicules petits ou non descendus (dans certains cas non obstructifs)",
      "Antécédents de vasectomie ou de chirurgie inguinale",
    ],
    howDiagnosed:
      "Confirmée par deux spermogrammes montrant zéro spermatozoïde. Les prises de sang (FSH, testostérone, LH) aident à différencier obstructif et non obstructif. Un test génétique (caryotype, mutations CFTR, microdélétion du chromosome Y) est recommandé. L'échographie testiculaire évalue le volume et la structure testiculaire.",
    impactOnFertility:
      "La conception naturelle n'est pas possible. Cependant, les spermatozoïdes peuvent souvent être prélevés chirurgicalement pour FIV-ICSI. L'azoospermie obstructive a le meilleur pronostic — du sperme est habituellement trouvé. Les cas non obstructifs ont des taux de récupération variables.",
    prevalence:
      "Touche 1 % de tous les hommes et 10 à 15 % des hommes infertiles.",
    treatmentOptions: [
      "Micro-TESE (extraction microchirurgicale testiculaire) — étalon-or pour les non obstructifs",
      "TESA ou PESA (pour les causes obstructives)",
      "Reperméabilisation post-vasectomie (si applicable)",
      "FIV-ICSI avec spermatozoïdes prélevés chirurgicalement",
      "Don de sperme (si aucun spermatozoïde ne peut être prélevé)",
      "Traitement hormonal (pour certains cas non obstructifs)",
    ],
    prognosis:
      "Azoospermie obstructive : taux de récupération de sperme > 95 %. Non obstructive : la micro-TESE trouve du sperme dans 40 à 60 % des cas. Quand du sperme est trouvé, les taux de fécondation et de grossesse par ICSI sont comparables aux autres causes de facteur masculin.",
  },
});

frCondition("dx-varicocele", {
  title: "Varicocèle",
  subtitle:
    "Veines dilatées dans le scrotum affectant la production spermatique",
  body: {
    conditionName: "Varicocèle",
    description:
      "Une varicocèle est une dilatation des veines à l'intérieur du scrotum (plexus pampiniforme), similaire aux varices des jambes. Les veines dilatées augmentent la température testiculaire et créent un stress oxydatif, qui peuvent altérer la production, la mobilité, et l'intégrité de l'ADN des spermatozoïdes. Les varicocèles sont la cause identifiable la plus courante d'infertilité masculine et, surtout, la plus corrigeable.",
    symptoms: [
      "Souvent asymptomatique — découverte au bilan de fertilité",
      "Douleur sourde ou sensation de lourdeur dans le scrotum (pire en station debout ou à l'effort)",
      "Veines dilatées visibles ou palpables (aspect en « sac de vers »)",
      "Atrophie testiculaire (testicule plus petit) du côté atteint",
      "Paramètres spermatiques anormaux",
    ],
    howDiagnosed:
      "L'examen physique par un urologue est la méthode principale — les varicocèles sont gradées de I à III selon la palpabilité. L'échographie scrotale avec Doppler confirme le diagnostic et mesure le diamètre veineux. Le spermogramme évalue l'impact fonctionnel. Généralement du côté gauche (85 %) en raison de l'anatomie veineuse.",
    impactOnFertility:
      "Les varicocèles sont trouvées chez environ 35 à 40 % des hommes en infertilité primaire et jusqu'à 80 % en infertilité secondaire. Elles causent un déclin progressif des paramètres spermatiques au fil du temps. Cependant, toutes les varicocèles ne causent pas une infertilité — beaucoup d'hommes avec varicocèle ont une fertilité normale.",
    prevalence:
      "Présente chez environ 15 à 20 % de tous les hommes et 35 à 40 % des hommes infertiles.",
    treatmentOptions: [
      "Varicocélectomie microchirurgicale (réparation chirurgicale étalon-or)",
      "Embolisation percutanée (alternative peu invasive)",
      "Varicocélectomie cœlioscopique",
      "Compléments antioxydants en attendant la chirurgie ou en adjuvant",
      "IIU ou FIV / ICSI si la réparation chirurgicale n'améliore pas suffisamment les paramètres",
    ],
    prognosis:
      "Après varicocélectomie, les paramètres spermatiques s'améliorent chez 60 à 80 % des hommes en 3 à 6 mois. Les taux de grossesse spontanée s'améliorent de 30 à 40 % après réparation. Combiné aux techniques d'AMP, les résultats sont encore meilleurs. L'amélioration peut prendre 3 à 6 mois (un cycle complet de spermatogenèse).",
  },
});

// ─── Autoimmune & related ──────────────────────────────────────────

frCondition("dx-adenomyosis", {
  title: "Adénomyose",
  subtitle: "Du tissu endométrial pousse dans la paroi musculaire utérine",
  body: {
    conditionName: "Adénomyose",
    description:
      "L'adénomyose survient quand du tissu qui tapisse normalement l'utérus (endomètre) pousse dans la paroi musculaire de l'utérus (myomètre). Cela fait grossir l'utérus et peut entraîner des règles abondantes et douloureuses. Elle est de plus en plus reconnue comme un facteur important d'échec d'implantation et de fausses couches à répétition.",
    symptoms: [
      "Saignements menstruels abondants (ménorragies)",
      "Crampes menstruelles sévères qui s'aggravent avec l'âge",
      "Douleur pelvienne chronique ou pression",
      "Douleurs pendant les rapports (dyspareunie)",
      "Utérus agrandi, sensible à l'examen",
      "Ballonnements pendant les règles",
      "Difficultés à concevoir ou échecs d'implantation à répétition",
    ],
    howDiagnosed:
      "Suspectée à l'échographie endovaginale (myomètre hétérogène, paroi utérine asymétriquement épaissie, kystes myométriaux). L'IRM est l'examen non invasif le plus précis, montrant un épaississement de la zone jonctionnelle > 12 mm. Le diagnostic définitif demandait historiquement l'anatomopathologie d'une hystérectomie, mais l'imagerie est maintenant considérée fiable en pratique clinique.",
    impactOnFertility:
      "L'adénomyose altère la fertilité par plusieurs mécanismes : contractilité utérine perturbée affectant le transport spermatique, implantation altérée par modification de la jonction endomètre-myomètre, inflammation augmentée, et flux sanguin utérin anormal. Les études suggèrent qu'elle réduit les taux de réussite en FIV de 25 à 40 %, en particulier les taux d'implantation et de naissance vivante.",
    prevalence:
      "Touche environ 20 à 35 % des femmes, même si beaucoup de cas sont asymptomatiques. Plus fréquente après 30 ans et chez celles avec antécédents de chirurgie utérine.",
    treatmentOptions: [
      "Suppression par agoniste GnRH (2 à 3 mois) avant transfert d'embryon",
      "Contraceptifs oraux étendus ou prétraitement par progestatif",
      "FIV avec transfert d'embryon congelé (pour laisser l'utérus se reposer)",
      "Adénomyomectomie dans les cas focaux sévères",
      "Médicaments anti-inflammatoires pour soulager les symptômes",
      "Gestation pour autrui dans les cas sévères réfractaires",
    ],
    prognosis:
      "Avec un prétraitement adapté (en particulier suppression par agoniste GnRH avant TEC), les taux de grossesse s'améliorent significativement. L'adénomyose légère a un meilleur pronostic que la maladie diffuse sévère. Reconnaissance précoce et traitement ciblé sont essentiels pour améliorer les résultats.",
  },
});

frCondition("dx-recurrent-pregnancy-loss", {
  title: "Fausses couches à répétition",
  subtitle: "Deux pertes de grossesse consécutives ou plus avant 20 semaines",
  body: {
    conditionName: "Fausses couches à répétition (FCR)",
    description:
      "Les fausses couches à répétition se définissent comme deux pertes de grossesse cliniques consécutives ou plus avant 20 semaines de gestation. Alors qu'une fausse couche unique est courante (touchant 15 à 25 % des grossesses), les pertes à répétition touchent un groupe plus restreint et justifient souvent une investigation des causes sous-jacentes — même si dans environ la moitié des cas aucune cause spécifique n'est identifiée.",
    symptoms: [
      "Deux fausses couches consécutives ou plus",
      "Saignements et crampes en début de grossesse",
      "Passage de tissus",
      "Test de grossesse positif suivi d'une perte",
    ],
    howDiagnosed:
      "L'évaluation inclut : caryotype des deux partenaires (analyse chromosomique), évaluation de la cavité utérine (HSG, hystérosonographie, ou hystéroscopie), bilan de thrombophilie (anticorps antiphospholipides, Facteur V Leiden, mutation de la prothrombine), fonction thyroïdienne, prolactine, et HbA1c. Les produits de conception peuvent être envoyés pour analyse chromosomique.",
    impactOnFertility:
      "Les FCR ne veulent pas dire qu'un couple ne pourra pas avoir de grossesse réussie. Même sans traitement, les couples avec FCR inexpliquées ont 60 à 75 % de chances de grossesse réussie à la prochaine tentative. La FIV avec PGT-A peut aider quand des problèmes chromosomiques sont suspectés en sélectionnant des embryons euploïdes.",
    prevalence:
      "Touche environ 1 à 2 % des couples essayant de concevoir. Le risque augmente avec l'âge maternel et le nombre de pertes antérieures.",
    treatmentOptions: [
      "FIV avec PGT-A (test génétique préimplantatoire pour aneuploïdie)",
      "PGT-SR si une translocation équilibrée est trouvée chez l'un des partenaires",
      "Aspirine à faible dose et/ou héparine pour le syndrome des antiphospholipides",
      "Supplémentation en progestérone en début de grossesse",
      "Optimisation thyroïdienne si une hypothyroïdie subclinique est trouvée",
      "Correction chirurgicale des anomalies utérines si présentes",
      "Optimisation du mode de vie et soutien émotionnel",
    ],
    prognosis:
      "Le pronostic est généralement encourageant. Même avec 3 pertes antérieures et aucune cause identifiée, la chance d'une prochaine grossesse réussie est d'environ 60 à 75 %. Quand une cause traitable est trouvée (par exemple SAPL, dysfonction thyroïdienne), le traitement ciblé améliore significativement les résultats.",
  },
});

frCondition("dx-antiphospholipid-syndrome", {
  title: "Syndrome des antiphospholipides (SAPL)",
  subtitle:
    "Maladie auto-immune causant caillots sanguins et pertes de grossesse",
  body: {
    conditionName: "Syndrome des antiphospholipides (SAPL)",
    description:
      "Le syndrome des antiphospholipides est une maladie auto-immune où le corps produit des anticorps contre certaines protéines sanguines, augmentant la tendance à former des caillots. En grossesse, ces caillots peuvent se former dans les vaisseaux placentaires, menant à des pertes, une prééclampsie, et un retard de croissance. Le SAPL est l'une des causes les plus traitables de fausses couches à répétition.",
    symptoms: [
      "Fausses couches à répétition (surtout après 10 semaines)",
      "Caillots sanguins dans les veines ou artères (TVP, EP)",
      "Mort fœtale inexpliquée",
      "Prééclampsie sévère ou syndrome HELLP",
      "Insuffisance placentaire et retard de croissance",
      "Certaines patientes n'ont aucun symptôme en dehors de la grossesse",
    ],
    howDiagnosed:
      "Diagnostiqué par la présence d'anticorps antiphospholipides à deux occasions séparées d'au moins 12 semaines : anticoagulant lupique, anticorps anticardiolipine (IgG/IgM), et anti-bêta-2-glycoprotéine I (IgG/IgM). Il faut aussi un événement clinique (perte de grossesse, caillot, ou complication de grossesse).",
    impactOnFertility:
      "Le SAPL affecte surtout le maintien de la grossesse plutôt que la conception. Sans traitement, les taux de perte de grossesse sont très élevés (jusqu'à 90 % dans certaines études). Avec un traitement adapté, les taux de naissance vivante s'améliorent drastiquement, à 70 à 80 %.",
    prevalence:
      "Trouvé chez environ 5 à 15 % des femmes avec fausses couches à répétition. Touche environ 1 à 5 % de la population générale.",
    treatmentOptions: [
      "Aspirine à faible dose (commencée avant la conception)",
      "Héparine ou héparine de bas poids moléculaire (commencée au test de grossesse positif)",
      "Suivi rapproché avec échographies sériées et Dopplers",
      "Adressage en médecine fœto-maternelle pour gestion de grossesse à risque",
      "Corticostéroïdes dans les cas réfractaires",
      "La FIV peut être recommandée en cas d'infertilité concomitante",
    ],
    prognosis:
      "Avec traitement (aspirine + héparine), les taux de naissance vivante atteignent 70 à 80 %, une amélioration spectaculaire par rapport aux résultats sans traitement. Diagnostic précoce et début de traitement rapide sont critiques. Un suivi hématologique continu est recommandé même hors grossesse.",
  },
});

frCondition("dx-ashermans-syndrome", {
  title: "Syndrome d'Asherman",
  subtitle: "Synéchies intra-utérines (cicatrices) bloquant la cavité utérine",
  body: {
    conditionName: "Syndrome d'Asherman",
    description:
      "Le syndrome d'Asherman est une condition où du tissu cicatriciel (adhérences ou synéchies) se forme à l'intérieur de l'utérus, bloquant partiellement ou complètement la cavité utérine. Il se développe le plus souvent après une chirurgie utérine, en particulier un curetage (D&C) réalisé après fausse couche ou accouchement. Les adhérences peuvent empêcher l'implantation embryonnaire et les règles normales.",
    symptoms: [
      "Règles légères ou absentes (hypoménorrhée ou aménorrhée)",
      "Douleur pelvienne cyclique malgré l'absence de règles (si la cavité est obstruée)",
      "Difficultés à concevoir",
      "Fausses couches à répétition",
      "Placentation anormale lors des grossesses suivantes",
    ],
    howDiagnosed:
      "Suspecté quand les règles deviennent légères ou absentes après une chirurgie utérine. L'hystérosonographie peut montrer des défauts de remplissage. Le diagnostic et le traitement définitifs se font par hystéroscopie, qui permet la visualisation directe et la lyse des adhérences. L'HSG peut montrer des défauts de remplissage irréguliers.",
    impactOnFertility:
      "Les adhérences bloquent physiquement l'implantation embryonnaire et peuvent réduire le flux sanguin vers l'endomètre. Les adhérences légères peuvent ne causer aucun problème de fertilité, tandis que les cas sévères peuvent altérer significativement l'implantation. Après traitement chirurgical, beaucoup de femmes peuvent concevoir, même si l'endomètre peut rester fin.",
    prevalence:
      "Survient chez environ 13 à 20 % des femmes après un curetage pour fausse couche, et jusqu'à 40 % après curetage pour rétention de produits de conception.",
    treatmentOptions: [
      "Adhésiolyse hystéroscopique (retrait chirurgical du tissu cicatriciel)",
      "Ballon ou stent intra-utérin post-opératoire pour prévenir la récidive",
      "Traitement par œstrogènes pour favoriser la repousse de l'endomètre après chirurgie",
      "Hystéroscopie de contrôle pour évaluer le résultat et retirer les adhérences récidivantes",
      "FIV avec gestation pour autrui dans les cas sévères réfractaires",
    ],
    prognosis:
      "Asherman léger à modéré : bon pronostic après traitement hystéroscopique, avec des taux de grossesse de 60 à 80 %. Les cas sévères avec oblitération complète de la cavité ont des taux de réussite plus bas et peuvent nécessiter plusieurs chirurgies. La prévention par des techniques chirurgicales douces est de plus en plus mise en avant.",
  },
});

// ─── Müllerian / endometrial ────────────────────────────────────────

frCondition("dx-mullerian-anomalies", {
  title: "Anomalies utérines",
  subtitle:
    "Différences congénitales de forme utérine pouvant affecter la grossesse",
  body: {
    conditionName: "Anomalies müllériennes (anomalies utérines)",
    description:
      "Les anomalies müllériennes sont des différences congénitales de forme ou de structure de l'utérus, des trompes de Fallope, ou du vagin supérieur, résultant d'un développement ou d'une fusion incomplète des canaux de Müller pendant le développement embryonnaire. Elles vont d'un simple septum utérin à un utérus complètement dupliqué ou absent. La plupart sont découvertes fortuitement pendant un bilan de fertilité.",
    symptoms: [
      "Souvent aucun symptôme — découverte au bilan de fertilité",
      "Fausses couches à répétition (surtout au 2e trimestre)",
      "Accouchement prématuré",
      "Présentation par le siège",
      "Règles douloureuses dans certains types (par exemple hémivagin obstrué)",
    ],
    howDiagnosed:
      "Suspicion initiale sur l'HSG ou l'hystérosonographie montrant une forme de cavité anormale. L'échographie 3D est excellente pour la classification. L'IRM est l'étalon-or pour le diagnostic définitif et la caractérisation détaillée. L'hystéroscopie peut confirmer et traiter certaines anomalies (septum).",
    impactOnFertility:
      "L'impact varie selon le type. Un septum utérin (le plus courant, ~35 % des anomalies) est associé au taux de perte de grossesse le plus élevé (jusqu'à 60 %) mais est chirurgicalement corrigeable. Les utérus unicorne et bicorne augmentent le risque de naissance prématurée mais beaucoup de femmes conçoivent avec succès. Une absence utérine complète (MRKH) nécessite une gestation pour autrui.",
    prevalence:
      "Présentes chez environ 5 à 7 % de la population générale et jusqu'à 16 % des femmes avec fausses couches à répétition.",
    treatmentOptions: [
      "Résection hystéroscopique du septum (pour utérus septé — le plus courant et traitable)",
      "Cerclage cervical pour incompétence cervicale associée à certaines anomalies",
      "Suivi obstétrical rapproché pour le risque de naissance prématurée",
      "FIV si les trompes sont affectées",
      "Gestation pour autrui en cas d'absence utérine ou d'utérus non fonctionnel",
    ],
    prognosis:
      "Pour l'utérus septé, la correction chirurgicale améliore drastiquement les résultats (taux de grossesse passant d'environ 40 % à 75 à 85 %). Les utérus unicorne et bicorne ont des taux de grossesse raisonnables avec un suivi adapté. Les résultats dépendent fortement du type et de la sévérité.",
  },
});

frCondition("dx-thin-endometrium", {
  title: "Endomètre fin",
  subtitle: "Muqueuse utérine trop fine pour une implantation optimale",
  body: {
    conditionName: "Endomètre fin",
    description:
      "L'endomètre fin désigne une muqueuse utérine qui n'atteint pas une épaisseur suffisante (généralement < 7 mm) pour l'implantation embryonnaire malgré une préparation hormonale adéquate. Un endomètre sain de 7 à 14 mm avec un aspect trilaminaire (trois couches) offre le meilleur environnement pour l'implantation. Un endomètre fin persistant est une condition difficile qui peut être un obstacle au succès du transfert d'embryon.",
    symptoms: [
      "Règles peu abondantes",
      "Échecs d'implantation répétés malgré de bons embryons",
      "Muqueuse fine au suivi échographique en préparation de TEC",
      "Souvent aucun symptôme — découverte au suivi du cycle",
    ],
    howDiagnosed:
      "Mesuré à l'échographie endovaginale pendant la fenêtre péri-implantatoire. Considéré fin si < 7 mm malgré des niveaux d'œstrogène suffisants. L'aspect (trilaminaire vs homogène) est aussi évalué. Le flux sanguin vers l'endomètre peut être évalué par échographie Doppler. L'hystéroscopie peut éliminer adhérences ou autre pathologie de la cavité.",
    impactOnFertility:
      "Les taux d'implantation déclinent significativement quand l'épaisseur endométriale est inférieure à 7 mm. Les études montrent que les taux de grossesse baissent d'environ 25 % par mm en dessous de 8 mm. Cependant, des grossesses surviennent avec des muqueuses plus fines — le seuil n'est pas absolu. L'aspect et le flux sanguin peuvent être aussi importants que l'épaisseur.",
    prevalence: "Touche environ 5 à 12 % des femmes en cycle de FIV/TEC.",
    treatmentOptions: [
      "Supplémentation prolongée en œstrogène (doses plus élevées ou durée plus longue)",
      "Œstrogène vaginal (Estrace) ajouté au schéma oral ou par patch",
      "Aspirine à faible dose pour améliorer le flux sanguin",
      "Sildénafil (Viagra) en ovules vaginaux (hors AMM, améliore le flux utérin)",
      "Combinaison pentoxifylline et vitamine E",
      "G-CSF (facteur stimulant les granulocytes) en infusion intra-utérine",
      "PRP (plasma riche en plaquettes) en infusion intra-utérine",
      "Hystéroscopie pour éliminer et traiter les adhérences",
    ],
    prognosis:
      "Beaucoup de cas d'endomètre fin s'améliorent avec des protocoles adaptés. Quand la muqueuse atteint ≥ 7 mm avec un aspect trilaminaire, les résultats sont comparables aux cycles d'épaisseur normale. Les cas réfractaires (muqueuse restant < 6 mm malgré toutes les interventions) peuvent bénéficier d'envisager une gestation pour autrui.",
  },
});

// ─── Luteal phase ───────────────────────────────────────────────────

frCondition("dx-luteal-phase-defect", {
  title: "Insuffisance lutéale",
  subtitle: "Soutien en progestérone insuffisant après l'ovulation",
  body: {
    conditionName: "Insuffisance lutéale",
    description:
      "L'insuffisance lutéale survient quand le corps jaune (la structure laissée après l'ovulation) produit une progestérone insuffisante, ou que l'endomètre ne répond pas adéquatement à la progestérone. Cela donne une phase lutéale plus courte que 10 jours ou des taux de progestérone trop bas pour soutenir l'implantation et la grossesse précoce. Cela reste un diagnostic débattu en médecine de la reproduction — certains experts questionnent son existence comme entité clinique distincte — mais beaucoup de patientes sont diagnostiquées et traitées pour cela.",
    symptoms: [
      "Phase lutéale courte (moins de 10 jours entre l'ovulation et les règles)",
      "Pertes ou spotting avant le début des règles",
      "Difficultés à concevoir malgré une ovulation régulière",
      "Perte de grossesse précoce",
      "Spotting prémenstruel plusieurs jours avant le flux complet",
    ],
    howDiagnosed:
      "Historiquement diagnostiquée par biopsie endométriale montrant une maturation retardée, méthode aujourd'hui peu utilisée. Les approches actuelles incluent : mesures sériées de progestérone sérique en milieu de phase lutéale (généralement jour 21 à 23), suivi de la durée de la phase lutéale par BBT ou détection du pic LH, et suivi de l'épaisseur et de l'aspect endométrial à l'échographie. Une progestérone de milieu de phase lutéale en dessous de 10 ng/mL est considérée comme suspecte.",
    impactOnFertility:
      "Une progestérone inadéquate peut empêcher le bon développement endométrial, rendant l'implantation difficile ou causant une perte de grossesse très précoce (grossesse chimique). En cycle de FIV et de TEC, ce problème est efficacement contourné par un soutien en progestérone, qui est de pratique standard.",
    prevalence:
      "Estimée à 3 à 20 % des femmes infertiles, même si la vraie prévalence est débattue à cause de critères diagnostiques inconsistants.",
    treatmentOptions: [
      "Supplémentation en progestérone (ovules vaginaux, intramusculaire, ou oral)",
      "Citrate de clomifène ou létrozole pour améliorer la qualité de l'ovulation",
      "Injections d'hCG en phase lutéale pour soutenir le corps jaune",
      "Correction des causes sous-jacentes (dysfonction thyroïdienne, hyperprolactinémie)",
      "En FIV/TEC : les protocoles standard de soutien en progestérone traitent efficacement cela",
    ],
    prognosis:
      "Avec supplémentation en progestérone, les résultats sont généralement bons. En FIV et TEC, le soutien lutéal est systématiquement fourni, ce qui rend l'insuffisance lutéale non pertinente. Pour la conception naturelle, une supplémentation en progestérone commencée après ovulation confirmée peut significativement améliorer les taux d'implantation.",
  },
});

// ─── Cervical ───────────────────────────────────────────────────────

frCondition("dx-cervical-stenosis", {
  title: "Sténose cervicale",
  subtitle:
    "Canal cervical rétréci pouvant compliquer les procédures de fertilité",
  body: {
    conditionName: "Sténose cervicale",
    description:
      "La sténose cervicale est un rétrécissement du canal cervical — le passage entre le vagin et l'utérus. Elle peut être congénitale (présente à la naissance) ou acquise après des procédures comme une conisation, une LEEP, une cautérisation cervicale, ou une radiothérapie. Elle n'empêche pas toujours directement la conception, mais peut rendre les procédures intra-utérines (IIU, transfert d'embryon, HSG) techniquement difficiles et peut altérer le transport spermatique.",
    symptoms: [
      "Tentatives de transfert d'embryon difficiles ou douloureuses",
      "Difficulté à passer un cathéter pendant une IIU ou une HSG",
      "Règles légères ou absentes (si le canal est sévèrement rétréci)",
      "Règles douloureuses avec crampes (sang menstruel ne pouvant pas s'écouler librement)",
      "Souvent aucun symptôme — découverte pendant une procédure",
    ],
    howDiagnosed:
      "Généralement découverte pendant une procédure quand le cathéter ne peut pas passer facilement à travers le canal cervical. Un transfert simulé (mock transfer) est spécifiquement conçu pour identifier ce problème avant un vrai cycle de transfert. Des antécédents de chirurgie cervicale (LEEP, conisation) augmentent la suspicion.",
    impactOnFertility:
      "La sténose cervicale peut altérer le transport spermatique naturel et rendre les procédures intra-utérines techniquement difficiles. Un transfert d'embryon difficile est associé à des taux de grossesse plus bas en raison de la manipulation du cathéter, des saignements, et des contractions utérines potentielles. Identifier et traiter la sténose avant le cycle de transfert est essentiel.",
    prevalence:
      "Peu fréquente dans la population générale. Plus fréquente chez les femmes avec antécédents de chirurgie cervicale — LEEP / conisation augmente le risque significativement.",
    treatmentOptions: [
      "Dilatation cervicale avant le cycle de transfert (sous anesthésie ou au cabinet)",
      "Laminaires ou dilatateurs osmotiques placés la veille du transfert",
      "Utilisation d'un stylet ou d'un cathéter plus rigide pendant le transfert",
      "Transfert simulé pour cartographier l'anatomie et planifier l'approche",
      "Hystéroscopie pour évaluer et dilater sous vision directe",
      "Dans les cas réfractaires sévères, transfert d'embryon trans-myométrial (rare)",
    ],
    prognosis:
      "Avec une bonne préparation (dilatation cervicale et transfert simulé), la plupart des cas de sténose cervicale sont gérables et n'impactent pas significativement les résultats de FIV. La clé est d'identifier le problème avant le jour du transfert pour éviter un transfert difficile ou annulé.",
  },
});
